// Block Jam level generator (reverse-move construction).
//
// We build a level by playing the game BACKWARDS from an empty board:
//  - reverse-exit: a block of a gate's color appears at that gate and slides
//    inward (its forward move is "exit through the gate");
//  - reverse-slide: an existing block is slid backward through empty cells (its
//    forward move is "slide back to where it was").
// Replaying those inverse moves in reverse order is therefore ALWAYS a valid
// solution, so the level is solvable by construction. The reverse-slides are what
// create real difficulty: forward, blocks sit in each other's exit paths and must
// be slid out of the way in the right order.

import {
  type Block,
  type Color,
  type Dir,
  type Gate,
  type Level,
  type Move,
  type Orient,
  type Side,
  allowedDirs,
  blockCells,
  cloneLevelState,
  legalMoves,
  movesForBlock,
  realisticSolveStats,
  replaySolution,
} from "./engine";

export function makeRng(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const PALETTE: Color[] = ["red", "blue", "green", "yellow", "purple", "orange", "cyan", "pink"];
const VEC: Record<Dir, [number, number]> = { up: [-1, 0], down: [1, 0], left: [0, -1], right: [0, 1] };
const OPP: Record<Dir, Dir> = { up: "down", down: "up", left: "right", right: "left" };
const GATE_EXIT_DIR: Record<Side, Dir> = { right: "right", left: "left", top: "up", bottom: "down" };
const GATE_INWARD: Record<Side, [number, number]> = { right: [0, -1], left: [0, 1], bottom: [-1, 0], top: [1, 0] };

function pick<T>(rng: () => number, arr: T[]): T {
  return arr[Math.floor(rng() * arr.length)];
}
function randInt(rng: () => number, lo: number, hi: number): number {
  return lo + Math.floor(rng() * (hi - lo + 1));
}
function shuffle<T>(arr: T[], rng: () => number): T[] {
  const a = arr.slice();
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

interface GenOptions {
  w: number;
  h: number;
  colors: number;
  blocks: number; // target block count
  maxLen: number;
  scramble: number; // number of reverse-slides during construction
  polish: number; // difficulty hill-climbing steps (targeted reverse-slides kept only if they measurably harden the level)
  minBlocks: number;
  secondGateChance: number; // chance a color gets a second gate (escape hatches make levels easier)
  targetForced: number; // hard gate: at least this many forced slides in the efficient solve
  minBlocked: number; // hard gate: at least this many blocks must NOT be exitable at the start
  minUnlockPhases: number; // hard gate: times a good player is fully stuck and must plan slides
  locks: number; // pinwheel locks to place first (each provably forces >=1 slide)
  walls: number; // static obstacle cells
  iceChance: number; // 0..1 chance per eligible block to become an ice block
  rainbowChance: number; // 0..1 chance per spawned block to become rainbow
  seed: number;
}

function key(r: number, c: number): number {
  return r * 1000 + c;
}
function occExcept(blocks: Block[], walls: Set<number>, exceptId: number): Set<number> {
  const s = new Set<number>(walls);
  for (const b of blocks) {
    if (b.id === exceptId) continue;
    for (const [r, c] of blockCells(b)) s.add(key(r, c));
  }
  return s;
}
function maxFreeSlide(
  blocks: Block[],
  walls: Set<number>,
  w: number,
  h: number,
  b: Block,
  dir: Dir,
): number {
  const [dr, dc] = VEC[dir];
  const occ = occExcept(blocks, walls, b.id);
  const cells = blockCells(b);
  let s = 0;
  for (;;) {
    const ns = s + 1;
    let ok = true;
    for (const [r, c] of cells) {
      const nr = r + dr * ns;
      const nc = c + dc * ns;
      if (nr < 0 || nr >= h || nc < 0 || nc >= w || occ.has(key(nr, nc))) {
        ok = false;
        break;
      }
    }
    if (!ok) break;
    s = ns;
  }
  return s;
}

/** Would this gate give `b` an alternate way out along its own lane? */
function gateOnBlockLane(g: Gate, b: Block): boolean {
  if (g.color !== b.color) return false;
  if (b.o === "h") return (g.side === "left" || g.side === "right") && g.index === b.r;
  return (g.side === "top" || g.side === "bottom") && g.index === b.c;
}

function buildGates(
  rng: () => number,
  o: GenOptions,
  colors: Color[],
  used: Set<string>,
  lockBlocks: Block[],
): Gate[] {
  const gates: Gate[] = [];
  for (const color of colors) {
    const count = rng() < o.secondGateChance ? 2 : 1;
    for (let i = 0; i < count; i++) {
      for (let attempt = 0; attempt < 20; attempt++) {
        const side = pick(rng, ["top", "bottom", "left", "right"] as Side[]);
        const limit = side === "top" || side === "bottom" ? o.w : o.h;
        const index = randInt(rng, 0, limit - 1);
        const k = `${side}:${index}`;
        if (used.has(k)) continue;
        const gate: Gate = { side, index, color };
        // A same-color gate on a lock block's lane would let it escape the lock.
        if (lockBlocks.some((b) => gateOnBlockLane(gate, b))) continue;
        used.add(k);
        gates.push(gate);
        break;
      }
    }
  }
  return gates;
}

/**
 * Scatter static wall cells, avoiding the cell directly inside each gate (a wall
 * there would make the gate permanently unreachable).
 */
function buildWalls(rng: () => number, o: GenOptions, gates: Gate[], protectedCells: Set<number>): Set<number> {
  // Walls are sampled from the interior ring [1..size-2], so protect the interior
  // cell one step inward from each gate (the border cell itself is never sampled).
  const forbidden = new Set<number>(protectedCells);
  for (const g of gates) {
    if (g.side === "top") forbidden.add(key(1, g.index));
    else if (g.side === "bottom") forbidden.add(key(o.h - 2, g.index));
    else if (g.side === "left") forbidden.add(key(g.index, 1));
    else forbidden.add(key(g.index, o.w - 2));
  }
  const walls = new Set<number>();
  let guard = 0;
  while (walls.size < o.walls && guard++ < 60) {
    const r = randInt(rng, 1, o.h - 2); // keep the border ring clear for gates
    const c = randInt(rng, 1, o.w - 2);
    const k = key(r, c);
    if (forbidden.has(k) || walls.has(k)) continue;
    walls.add(k);
  }
  return walls;
}

/** Reverse-exit: spawn a block at a gate and slide it inward. Returns the block + its forward exit move. */
function tryAddBlock(
  rng: () => number,
  o: GenOptions,
  blocks: Block[],
  walls: Set<number>,
  gates: Gate[],
  gate: Gate,
  id: number,
): { block: Block; forward: Move } | null {
  const horizontal = gate.side === "left" || gate.side === "right";
  const maxLen = Math.min(o.maxLen, horizontal ? o.w : o.h);
  const len = randInt(rng, 1, Math.max(1, maxLen));
  const line = gate.index;

  let r: number;
  let c: number;
  if (gate.side === "right") {
    r = line;
    c = o.w - len;
  } else if (gate.side === "left") {
    r = line;
    c = 0;
  } else if (gate.side === "bottom") {
    r = o.h - len;
    c = line;
  } else {
    r = 0;
    c = line;
  }
  const orient: Orient = horizontal ? "h" : "v";
  // Occasionally spawn a rainbow block (it exits through any gate, incl. this one).
  const color = rng() < o.rainbowChance ? "rainbow" : gate.color;
  const flush: Block = { id, color, r, c, len, o: orient };

  const occ = occExcept(blocks, walls, id);
  for (const [cr, cc] of blockCells(flush)) {
    if (cr < 0 || cr >= o.h || cc < 0 || cc >= o.w || occ.has(key(cr, cc))) return null;
  }

  // How far inward CAN the block slide?
  const [dr, dc] = GATE_INWARD[gate.side];
  let reach = 0;
  for (;;) {
    const ns = reach + 1;
    if (ns > (horizontal ? o.w - len : o.h - len)) break;
    let ok = true;
    for (const [cr, cc] of blockCells(flush)) {
      const nr = cr + dr * ns;
      const nc = cc + dc * ns;
      if (nr < 0 || nr >= o.h || nc < 0 || nc >= o.w || occ.has(key(nr, nc))) {
        ok = false;
        break;
      }
    }
    if (!ok) break;
    reach = ns;
  }

  // Prefer stopping ON another block's exit path: that is where near-locks form,
  // which a later reverse-slide can close into a genuine ordering puzzle.
  const targets = exitPathCellSet(o, gates, blocks, id);
  let steps = randInt(rng, 0, reach);
  let bestHits = -1;
  for (let s = 0; s <= reach; s++) {
    let hits = 0;
    for (const [cr, cc] of blockCells(flush)) {
      if (targets.has(key(cr + dr * s, cc + dc * s))) hits++;
    }
    if (hits > bestHits || (hits === bestHits && rng() < 0.5)) {
      bestHits = hits;
      steps = s;
    }
  }

  const block: Block = { ...flush, r: flush.r + dr * steps, c: flush.c + dc * steps };
  const forward: Move = { blockId: id, dir: GATE_EXIT_DIR[gate.side], type: "exit" };
  return { block, forward };
}

const SIDE_OF_DIR: Record<Dir, Side> = { up: "top", down: "bottom", left: "left", right: "right" };

/**
 * All cells that OTHER blocks must traverse to reach a matching gate (their
 * exit paths). Landing a scrambled block on these cells is what creates real
 * ordering pressure: the covered block cannot leave until the blocker moves,
 * and if the blocker cannot exit yet either, the player must plan slides.
 */
function exitPathCellSet(o: GenOptions, gates: Gate[], blocks: Block[], exceptId: number): Set<number> {
  const set = new Set<number>();
  for (const y of blocks) {
    if (y.id === exceptId) continue;
    for (const d of allowedDirs(y)) {
      const side = SIDE_OF_DIR[d];
      const lane = y.o === "h" ? y.r : y.c;
      const gate = gates.find((g) => g.side === side && g.index === lane);
      if (!gate || (y.color !== "rainbow" && gate.color !== y.color)) continue;
      const [dr, dc] = VEC[d];
      // Walk from the block's leading edge to the border: those are its exit cells.
      let r = d === "down" ? y.r + y.len - 1 : y.r;
      let c = d === "right" ? y.c + y.len - 1 : y.c;
      for (;;) {
        r += dr;
        c += dc;
        if (r < 0 || r >= o.h || c < 0 || c >= o.w) break;
        set.add(key(r, c));
      }
    }
  }
  return set;
}

/** Reverse-slide: move a (non-exitable) block backward through empty cells,
 * preferring landing spots that cover another block's exit path. Returns its
 * forward slide move. */
function reverseSlide(
  rng: () => number,
  o: GenOptions,
  tempLevel: Level,
  walls: Set<number>,
  blocks: Block[],
  b: Block,
): Move | null {
  // Never scramble a block that can currently exit - that would make the forward
  // "slide back" indistinguishable from an exit.
  if (movesForBlock(tempLevel, blocks, b).some((m) => m.type === "exit")) return null;

  const targets = exitPathCellSet(o, tempLevel.gates, blocks, b.id);
  let best: Array<{ back: Dir; k: number }> = [];
  let bestHits = -1;
  for (const back of shuffle(allowedDirs(b), rng)) {
    const max = maxFreeSlide(blocks, walls, o.w, o.h, b, back);
    const [dr, dc] = VEC[back];
    for (let k = 1; k <= max; k++) {
      let hits = 0;
      for (const [r, c] of blockCells(b)) {
        if (targets.has(key(r + dr * k, c + dc * k))) hits++;
      }
      if (hits > bestHits) {
        bestHits = hits;
        best = [{ back, k }];
      } else if (hits === bestHits) {
        best.push({ back, k });
      }
    }
  }
  if (best.length === 0) return null;

  const { back, k } = pick(rng, best);
  const orig = { r: b.r, c: b.c };
  const [dr, dc] = VEC[back];
  b.r += dr * k;
  b.c += dc * k;
  return { blockId: b.id, dir: OPP[back], type: "slide", to: orig };
}

interface Candidate {
  level: Level;
  solution: Move[];
}


/**
 * Freeze some blocks into ice. Block b's first forward move happens at solution
 * index i; if E exits happen strictly before i, then any melt <= E keeps the
 * constructed solution valid - the replay verification then re-proves it.
 */
function assignIce(rng: () => number, o: GenOptions, blocks: Block[], solution: Move[]): void {
  const firstUse = new Map<number, number>();
  const exitsBefore: number[] = [];
  let exits = 0;
  solution.forEach((mv, i) => {
    if (!firstUse.has(mv.blockId)) firstUse.set(mv.blockId, i);
    exitsBefore[i] = exits;
    if (mv.type === "exit") exits++;
  });
  let iceCount = 0;
  for (const b of shuffle(blocks, rng)) {
    if (iceCount >= 3) break;
    if (b.color === "rainbow") continue; // keep the two specials visually distinct
    const idx = firstUse.get(b.id);
    if (idx == null) continue;
    const eb = exitsBefore[idx];
    if (eb >= 1 && rng() < o.iceChance) {
      b.melt = randInt(rng, 1, Math.min(eb, 3));
      iceCount++;
    }
  }
}

interface LockSpec {
  blocks: Block[];
  gates: Gate[];
  /** Forward solution of the lock alone: one break slide, then the four exits. */
  solution: Move[];
  /** Motif cells + exit lanes + break path: keep walls and other locks off these. */
  protect: Set<number>;
}

/**
 * A parameterized, PROVABLY breakable pinwheel lock: four blocks in a rotational
 * blocking cycle - X pins Y pins Z pins W pins X - so NONE of them can exit
 * until X makes room with a genuine slide. Every lock therefore guarantees at
 * least one forced slide and one planning moment, by construction.
 *
 * Canonical geometry (mirrored horizontally/vertically for variety):
 *   - X horizontal, rightmost cell (a, p), RIGHT gate on row a;
 *   - Y vertical, col qy = X's leftmost col, ending at row a-1, BOTTOM gate col qy
 *     (its downward path passes (a, qy), covered by X);
 *   - Z horizontal on row a-1, starting right of qy, LEFT gate row a-1
 *     (its leftward path passes (a-1, qy), covered by Y);
 *   - W vertical, col qw in Z's span with qw >= p+2, starting at row a, TOP gate
 *     col qw (its upward path passes (a-1, qw), covered by Z; and it covers
 *     (a, qw) on X's rightward path, closing the cycle).
 * Forward solution: slide X right 1 (clears (a, qy)), exit Y, exit Z, exit W, exit X.
 */
function makePinwheel(
  rng: () => number,
  o: GenOptions,
  colors: Color[],
  idStart: number,
  occupied: Set<number>,
  usedGateSlots: Set<string>,
  existingBlocks: Block[],
  existingGates: Gate[],
): LockSpec | null {
  for (let attempt = 0; attempt < 40; attempt++) {
    const lx = randInt(rng, 1, 2);
    const ly = randInt(rng, 1, 2);
    const lz = randInt(rng, 1, 2);
    const lw = randInt(rng, 1, 2);

    // Canonical coordinates (see doc comment).
    const a = randInt(rng, Math.max(1, ly), o.h - lw); // X's row; Y fits above, W below
    const pMin = lx - 1; // X leftmost >= 0
    const pMax = o.w - 4; // leave room for qw >= p+2 and W's lane
    if (pMax < pMin) continue;
    const p = randInt(rng, pMin, pMax);
    const qy = p - lx + 1;
    const z0 = randInt(rng, qy + 1, Math.min(p + 2, o.w - lz));
    const qwMin = Math.max(z0, p + 2);
    const qwMax = z0 + lz - 1;
    if (qwMin > qwMax || qwMax > o.w - 1) continue;
    const qw = randInt(rng, qwMin, qwMax);

    const fH = rng() < 0.5;
    const fV = rng() < 0.5;
    const tr = (r: number): number => (fV ? o.h - 1 - r : r);
    const tc = (c: number): number => (fH ? o.w - 1 - c : c);
    // Transform a canonical block (top-left + len + orient) through the mirrors.
    const txBlock = (r0: number, c0: number, len: number, or: Orient): { r: number; c: number } => ({
      r: or === "v" && fV ? o.h - 1 - (r0 + len - 1) : tr(r0),
      c: or === "h" && fH ? o.w - 1 - (c0 + len - 1) : tc(c0),
    });
    const txDir = (d: Dir): Dir =>
      d === "left" || d === "right" ? (fH ? OPP[d] : d) : fV ? OPP[d] : d;
    const txSide = (s: Side): Side =>
      s === "left" || s === "right" ? (fH ? (s === "left" ? "right" : "left") : s) : fV ? (s === "top" ? "bottom" : "top") : s;

    const defs = [
      { r0: a, c0: qy, len: lx, or: "h" as Orient, gate: { side: "right" as Side, index: a }, exit: "right" as Dir },
      { r0: a - ly, c0: qy, len: ly, or: "v" as Orient, gate: { side: "bottom" as Side, index: qy }, exit: "down" as Dir },
      { r0: a - 1, c0: z0, len: lz, or: "h" as Orient, gate: { side: "left" as Side, index: a - 1 }, exit: "left" as Dir },
      { r0: a, c0: qw, len: lw, or: "v" as Orient, gate: { side: "top" as Side, index: qw }, exit: "up" as Dir },
    ];

    const blocks: Block[] = defs.map((d, i) => {
      const pos = txBlock(d.r0, d.c0, d.len, d.or);
      return { id: idStart + i, color: colors[i], r: pos.r, c: pos.c, len: d.len, o: d.or };
    });

    // Overlap check against the board and previously placed locks.
    const cells = new Set<number>();
    let ok = true;
    for (const b of blocks) {
      for (const [r, c] of blockCells(b)) {
        const k = key(r, c);
        if (r < 0 || r >= o.h || c < 0 || c >= o.w || cells.has(k) || occupied.has(k)) ok = false;
        cells.add(k);
      }
    }
    if (!ok) continue;

    // Gate slots must be free (and stay unique per (side,index)).
    const gates: Gate[] = defs.map((d, i) => {
      const side = txSide(d.gate.side);
      const index = d.gate.side === "left" || d.gate.side === "right" ? tr(d.gate.index) : tc(d.gate.index);
      return { side, index, color: colors[i] };
    });
    if (gates.some((g) => usedGateSlots.has(`${g.side}:${g.index}`))) continue;
    // Color-lane safety across locks: no lock block may gain an alternate exit
    // through another lock's same-color gate.
    if (gates.some((g) => existingBlocks.some((b) => gateOnBlockLane(g, b)))) continue;
    if (blocks.some((b) => existingGates.some((g) => gateOnBlockLane(g, b)))) continue;

    // Protected cells: motif + each block's full lane to its gate (covers the
    // break path too). Other locks' blocks and walls must stay off these.
    const protect = new Set<number>(cells);
    const laneCells = (b: Block, d: Dir): void => {
      const [dr, dc] = VEC[d];
      let r = d === "down" ? b.r + b.len - 1 : b.r;
      let c = d === "right" ? b.c + b.len - 1 : b.c;
      for (;;) {
        r += dr;
        c += dc;
        if (r < 0 || r >= o.h || c < 0 || c >= o.w) break;
        protect.add(key(r, c));
      }
    };
    defs.forEach((d, i) => laneCells(blocks[i], txDir(d.exit)));

    // NOTE: this lock's lanes may freely overlap EARLIER locks' cells - locks
    // crack in placement order, so by the time these lanes are traversed the
    // earlier locks are gone. Only this lock's BLOCKS must stay off earlier
    // locks' protected cells (checked above via `occupied`), and walls must
    // stay off every lane (the caller adds `protect` to `occupied` for that).

    const xDest = txBlock(a, qy + 1, lx, "h");
    const solution: Move[] = [
      { blockId: blocks[0].id, dir: txDir("right"), type: "slide", to: xDest },
      { blockId: blocks[1].id, dir: txDir("down"), type: "exit" },
      { blockId: blocks[2].id, dir: txDir("left"), type: "exit" },
      { blockId: blocks[3].id, dir: txDir("up"), type: "exit" },
      { blockId: blocks[0].id, dir: txDir("right"), type: "exit" },
    ];

    return { blocks, gates, solution, protect };
  }
  return null;
}

/** Live construction state: blocks/rec are mutable, tempLevel shares `blocks`. */
interface BuildCtx {
  o: GenOptions;
  rng: () => number;
  tempLevel: Level;
  walls: Set<number>;
  wallsArr: Array<[number, number]>;
  blocks: Block[];
  rec: Move[];
  /** Blocks belonging to pinwheel locks: never scrambled, so the locks stay intact. */
  lockIds: Set<number>;
  /** Forward tail of the solution: the locks' break slides + exits, in lock order. */
  lockSolution: Move[];
}

// Lock blocks draw from the back of the palette, filler colors from the front,
// so a filler gate can never offer a lock block an alternate exit.
const LOCK_COLORS = PALETTE.slice(4);

/** Locks first, then phases A + B of reverse construction around them. */
function buildRaw(o: GenOptions): BuildCtx {
  const rng = makeRng(o.seed);

  // Phase 0: place the pinwheel locks (the guaranteed "real puzzle" cores).
  const lockBlocks: Block[] = [];
  const lockGates: Gate[] = [];
  const lockSolution: Move[] = [];
  const lockIds = new Set<number>();
  const occupied = new Set<number>();
  const usedGateSlots = new Set<string>();
  let id = 1;
  for (let li = 0; li < o.locks; li++) {
    const colors = shuffle(LOCK_COLORS, rng) as Color[];
    const spec = makePinwheel(rng, o, colors, id, occupied, usedGateSlots, lockBlocks, lockGates);
    if (!spec) break; // no room for another lock on this board
    lockBlocks.push(...spec.blocks);
    lockGates.push(...spec.gates);
    lockSolution.push(...spec.solution);
    for (const b of spec.blocks) lockIds.add(b.id);
    for (const k of spec.protect) occupied.add(k);
    for (const g of spec.gates) usedGateSlots.add(`${g.side}:${g.index}`);
    id += spec.blocks.length;
  }

  const fillerColors = PALETTE.slice(0, Math.min(o.colors, PALETTE.length - LOCK_COLORS.length)) as Color[];
  const gates = [...lockGates, ...buildGates(rng, o, fillerColors, usedGateSlots, lockBlocks)];
  const walls = buildWalls(rng, o, gates, occupied);
  const wallsArr: Array<[number, number]> = [...walls].map((k) => [Math.floor(k / 1000), k % 1000]);
  const blocks: Block[] = [...lockBlocks];
  // One stable level object for in-construction move checks (blocks is shared).
  const tempLevel: Level = { w: o.w, h: o.h, blocks, gates, walls: wallsArr };
  const rec: Move[] = [];
  const ctx: BuildCtx = { o, rng, tempLevel, walls, wallsArr, blocks, rec, lockIds, lockSolution };
  let guard = 0;

  // Scrambling may only touch filler blocks: moving a lock block would open the lock.
  const scramblePool = (): Block[] => blocks.filter((b) => !lockIds.has(b.id));

  // Phase A: introduce filler blocks (with occasional interleaved scrambles).
  const targetFiller = o.blocks;
  let added = 0;
  while (added < targetFiller && guard++ < targetFiller * 15) {
    const gate = pick(rng, gates);
    const res = tryAddBlock(rng, o, blocks, walls, gates, gate, id);
    if (!res) continue;
    blocks.push(res.block);
    rec.push(res.forward);
    id++;
    added++;
    const pool = scramblePool();
    if (pool.length > 1 && rng() < 0.45) {
      const mv = reverseSlide(rng, o, tempLevel, walls, blocks, pick(rng, pool));
      if (mv) rec.push(mv);
    }
  }

  // Phase B: scramble - these become the FIRST forward moves, so the player must
  // slide blocks out of the way before anything can exit.
  let done = 0;
  let sg = 0;
  while (done < o.scramble && sg++ < o.scramble * 10 && scramblePool().length > 0) {
    const mv = reverseSlide(rng, o, tempLevel, walls, blocks, pick(rng, scramblePool()));
    if (mv) done++;
    if (mv) rec.push(mv);
  }

  return ctx;
}

interface Hardness {
  solvable: boolean;
  forced: number;
  unlock: number;
  blocked: number;
  val: number;
}

/** Measure current construction state difficulty (no ice yet). */
function measureCtx(ctx: BuildCtx): Hardness {
  const exitable = new Set(
    legalMoves(ctx.tempLevel, ctx.blocks)
      .filter((m) => m.type === "exit")
      .map((m) => m.blockId),
  ).size;
  const blocked = ctx.blocks.length - exitable;
  const stats = realisticSolveStats(ctx.tempLevel);
  if (!stats) return { solvable: false, forced: 0, unlock: 0, blocked, val: -1 };
  const forced = stats.moves - ctx.blocks.length;
  // The small "blocked" term gives non-improving steps a gradient toward
  // near-locks even while forced/unlock are still zero.
  return { solvable: true, forced, unlock: stats.unlockPhases, blocked, val: forced * 30 + stats.unlockPhases * 50 + blocked };
}

interface SlideOption {
  b: Block;
  back: Dir;
  k: number;
}

/** Every legal single reverse-slide in the current state. */
function allReverseSlides(ctx: BuildCtx): SlideOption[] {
  const out: SlideOption[] = [];
  for (const b of ctx.blocks) {
    if (ctx.lockIds.has(b.id)) continue; // never disturb a lock
    // Scrambling a currently-exitable block would make its forward "slide back"
    // indistinguishable from an exit.
    if (movesForBlock(ctx.tempLevel, ctx.blocks, b).some((m) => m.type === "exit")) continue;
    for (const back of allowedDirs(b)) {
      const max = maxFreeSlide(ctx.blocks, ctx.walls, ctx.o.w, ctx.o.h, b, back);
      for (let k = 1; k <= max; k++) out.push({ b, back, k });
    }
  }
  return out;
}

function applySlideOption(m: SlideOption): Move {
  const orig = { r: m.b.r, c: m.b.c };
  const [dr, dc] = VEC[m.back];
  m.b.r += dr * m.k;
  m.b.c += dc * m.k;
  return { blockId: m.b.id, dir: OPP[m.back], type: "slide", to: orig };
}

/**
 * Difficulty polish: exhaustive first-improvement ascent over single
 * reverse-slides. Blocking CYCLES (pinwheel-style locks where X pins Y pins Z
 * ... pins X) are what force real slides, and random scrambling almost never
 * completes one - but given the near-locks created by the targeted spawning, a
 * single well-chosen reverse-slide often can. So each step tries ALL legal
 * reverse-slides until one measurably hardens the level; if none does, it takes
 * one neutral drift step and tries again. Every kept reverse-slide prepends its
 * forward move to the solution, so solvability is preserved exactly.
 */
function polishCtx(ctx: BuildCtx): void {
  const o = ctx.o;
  if (o.polish <= 0 || ctx.blocks.length < 2) return;
  let cur = measureCtx(ctx);
  if (!cur.solvable) return;

  for (let step = 0; step < o.polish; step++) {
    if (cur.forced >= o.targetForced && cur.unlock >= o.minUnlockPhases) return; // gates met
    const options = shuffle(allReverseSlides(ctx), ctx.rng);
    const neutrals: SlideOption[] = [];
    let improved = false;
    for (const m of options) {
      const mv = applySlideOption(m);
      const h = measureCtx(ctx);
      if (h.solvable && h.val > cur.val) {
        ctx.rec.push(mv);
        cur = h;
        improved = true;
        break; // first improvement wins; re-enumerate from the new state
      }
      m.b.r = mv.to!.r;
      m.b.c = mv.to!.c;
      if (h.solvable && h.val === cur.val) neutrals.push(m);
    }
    if (!improved) {
      if (neutrals.length === 0) return; // fully stuck: no useful move exists
      const m = pick(ctx.rng, neutrals);
      const mv = applySlideOption(m);
      ctx.rec.push(mv);
    }
  }
}

/** Freeze the construction into a playable candidate (ice + solution + snapshot). */
function finalize(ctx: BuildCtx): Candidate {
  // Forward play: first undo the scrambles/spawns (filler exits), then crack the locks.
  const solution = [...ctx.rec.slice().reverse(), ...ctx.lockSolution];
  assignIce(ctx.rng, ctx.o, ctx.blocks, solution);
  const level: Level = {
    w: ctx.o.w,
    h: ctx.o.h,
    blocks: ctx.blocks.map((b) => ({ ...b })),
    gates: ctx.tempLevel.gates,
    par: solution.length,
  };
  if (ctx.wallsArr.length) level.walls = ctx.wallsArr;
  return { level, solution };
}

/** Difficulty measurements of a verified candidate. */
interface Measured {
  cand: Candidate;
  blocked: number;
  forcedSlides: number;
  unlockPhases: number;
  score: number;
}

function measure(cand: Candidate): Measured {
  const stats = realisticSolveStats(cand.level);
  const par = stats?.moves ?? cand.solution.length;
  cand.level.par = par;

  // Three difficulty signals:
  //  - blocked: how many blocks are NOT exitable at the start (you must clear
  //    others first to open them up) - the "clear others first" feel;
  //  - forcedSlides: extra moves beyond one-exit-per-block (real planning);
  //  - unlockPhases: how often a good player is fully stuck and must work out a
  //    slide sequence - the purest measure of ordering depth.
  const start = cloneLevelState(cand.level);
  const exitableAtStart = new Set(
    legalMoves(cand.level, start)
      .filter((m) => m.type === "exit")
      .map((m) => m.blockId),
  ).size;
  const blocked = cand.level.blocks.length - exitableAtStart;
  const forcedSlides = par - cand.level.blocks.length;
  const unlockPhases = stats?.unlockPhases ?? 0;
  const score = blocked * 6 + forcedSlides * 12 + unlockPhases * 8 + cand.level.blocks.length;
  return { cand, blocked, forcedSlides, unlockPhases, score };
}

/**
 * Generate one solvable level whose difficulty is GUARANTEED, not just likely:
 * candidates are built by reverse construction (solvable by construction, then
 * re-proven by replaying the recorded solution) and only accepted when they meet
 * the hard gates in `base` (minBlocked / targetForced / minUnlockPhases). If no
 * candidate meets every gate within the attempt budget, the best-scoring
 * verified-solvable candidate is returned instead - the result is therefore
 * always solvable, and as puzzle-like as the parameters allow.
 */
export function generateLevel(base: GenOptions): Candidate {
  // Stage 1: many cheap raw constructions, measured once each.
  const raws: Array<{ ctx: BuildCtx; h: Hardness }> = [];
  for (let attempt = 0; attempt < 40; attempt++) {
    const ctx = buildRaw({ ...base, seed: base.seed + attempt * 7919 });
    if (ctx.blocks.length < base.minBlocks) continue;
    const h = measureCtx(ctx);
    if (!h.solvable) continue;
    raws.push({ ctx, h });
  }
  raws.sort((a, b) => b.h.val - a.h.val);

  // Stage 2: expensive exhaustive polish, but only on the most promising few.
  let bestGated: Measured | null = null;
  let bestVerified: Measured | null = null;
  for (const raw of raws.slice(0, 5)) {
    polishCtx(raw.ctx);
    const cand = finalize(raw.ctx);
    if (!replaySolution(cand.level, cand.solution)) continue;

    const m = measure(cand);
    if (!bestVerified || m.score > bestVerified.score) bestVerified = m;

    const meetsGates =
      m.blocked >= base.minBlocked &&
      m.forcedSlides >= base.targetForced &&
      m.unlockPhases >= base.minUnlockPhases;
    if (meetsGates) {
      bestGated = m;
      break;
    }
  }

  let chosen = (bestGated ?? bestVerified)?.cand ?? null;

  // Absolute solvability guarantee: if every attempt failed verification (only
  // possible in pathological parameter corners), rebuild without ice - a pure
  // reverse construction always replays - and keep trying fresh seeds.
  for (let extra = 0; !chosen && extra < 200; extra++) {
    const cand = finalize(buildRaw({ ...base, iceChance: 0, seed: base.seed + 999983 + extra * 104729 }));
    if (replaySolution(cand.level, cand.solution)) chosen = cand;
  }
  if (!chosen) throw new Error("blockjam generator: could not produce a verified level");

  measure(chosen); // (re)compute a fair par for the star rating
  return chosen;
}

/**
 * Infinite difficulty curve: works for ANY level number n >= 1, so levels can be
 * generated endlessly and deterministically (the seed is derived from n).
 *
 * Shape of the curve:
 *  - boards grow 5x5 -> 6x6 (n=6) -> 7x7 (n=25) and stay tight on purpose;
 *  - walls from level 6, rainbow blocks from 8, ice blocks from 10;
 *  - every 10th level is a "boss" (extra forced slides + an extra block);
 *  - the level right after a boss is a breather;
 *  - past the ramp the hard gates plateau at a stiff ceiling, and endless
 *    variety comes from the per-level seed.
 */
export function optionsForLevel(n: number): GenOptions {
  const size = n < 6 ? 5 : n < 25 ? 6 : 7;
  const isBoss = n >= 10 && n % 10 === 0;
  const isBreather = n > 10 && n % 10 === 1;

  // Pinwheel locks are the guaranteed puzzle cores: 1 from level 5, 2 on the
  // 7x7 boards. Bosses get one extra (dropped gracefully if it won't fit),
  // breathers one fewer.
  const baseLocks = n < 5 ? 0 : n < 25 ? 1 : 2;
  const locks = baseLocks + (isBoss ? 1 : 0) - (isBreather && baseLocks > 1 ? 1 : 0);

  const colors = Math.min(8, 2 + Math.floor(n / 5));
  const walls = n < 6 ? 0 : Math.min(size === 7 ? 5 : 3, 1 + Math.floor((n - 6) / 10));
  const maxFill = (size === 5 ? 7 : size === 6 ? 11 : 15) - Math.ceil(walls / 2);
  // `blocks` counts FILLER blocks; each lock adds 4 more on top.
  let blocks = Math.max(2, Math.min(maxFill, 4 + Math.floor(n / 1.7)) - 4 * locks);
  const maxLen = n < 4 ? 2 : 3;
  const scramble = Math.min(30, 5 + Math.floor(n * 0.9));
  const polish = n < 3 ? 0 : Math.min(40, 6 + n);
  // Escape hatches (second gates) fade out as difficulty rises.
  const secondGateChance = Math.max(0.15, 0.5 - n * 0.02);

  // Hard difficulty gates (see generateLevel). Each lock provably forces at
  // least one slide and one planning moment, so these are reliably satisfiable.
  // A boss's extra lock is intentionally NOT gated: if it fit, the level is
  // measurably harder; if not, the level still meets the normal bar.
  const targetForced = Math.max(0, locks - (isBoss ? 1 : 0));
  // Locks + protected lanes eat board space, so not every filler target fits;
  // require ~70% of the ambition rather than a near-exact count.
  const minBlocks = Math.max(3, Math.floor((blocks + 4 * locks) * 0.7));
  const minBlocked = Math.min(minBlocks - 1, Math.floor(n / 3));
  const minUnlockPhases = Math.min(locks, 2);

  const iceChance = n < 10 ? 0 : Math.min(0.5, 0.2 + (n - 10) * 0.015);
  const rainbowChance = n < 8 ? 0 : 0.06;
  return {
    w: size,
    h: size,
    colors,
    blocks,
    maxLen,
    scramble,
    polish,
    secondGateChance,
    minBlocks,
    targetForced,
    minBlocked,
    minUnlockPhases,
    locks,
    walls,
    iceChance,
    rainbowChance,
    seed: 1000 + n * 131,
  };
}

export function generateCampaign(count: number): Candidate[] {
  const levels: Candidate[] = [];
  for (let n = 1; n <= count; n++) levels.push(generateLevel(optionsForLevel(n)));
  return levels;
}
